import { useCallback, useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import { ISanitizedCompany } from 'types/company';
import { IUserResponse, Permissions } from 'types/user';

import { fetchUser } from 'services/api/next/user';
import { getIsUserInCompany } from 'services/user';

import { useUserStore } from 'stores/userStore';

export const useClearUser = () => {
  const setUser = useUserStore(state => state.setUser);

  const clearUser = useCallback(() => {
    setUser(null);
  }, [setUser]);

  return { clearUser };
};

export const useFetchUser = () => {
  const setUser = useUserStore(state => state.setUser);

  // Syncing the store here, rather than via a separate effect watching `data`,
  // guarantees the store is updated by the time any caller's `await refetch()`
  // resolves — callers (e.g. the sign-in handlers) that navigate right after
  // `refetch()` would otherwise race the store update against the next page's
  // first paint, sometimes missing the header avatar.
  const { data, refetch, isLoading } = useQuery<IUserResponse>(
    ['user'],
    fetchUser,
    {
      refetchOnWindowFocus: false,
      enabled: false,
      onSuccess: setUser,
    },
  );

  return { data, refetch, isLoading };
};

export const useProvideUser = () => {
  const { refetch } = useFetchUser();

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const useUser = () => {
  const user = useUserStore(state => state.user);
  const setUser = useUserStore(state => state.setUser);

  return { user, setUser };
};

export const useUserPermissions = (company: ISanitizedCompany) => {
  const user = useUserStore(state => state.user);

  const isUserInCompany = getIsUserInCompany(user, company.id);

  const canCreateStories = user?.permissions
    ? user.permissions.includes(Permissions.CREATE_STORY)
    : undefined;
  const canEditCompanyStories = user?.permissions
    ? user.permissions.includes(Permissions.EDIT_COMPANY_STORY)
    : undefined;
  const canEditUsers = user?.permissions
    ? user.permissions.includes(Permissions.EDIT_USER)
    : undefined;

  return {
    canCreateStories: isUserInCompany && canCreateStories,
    canEditCompanyStories,
    canEditUsers: isUserInCompany && canEditUsers,
  };
};
